import React from 'react';
import { Calendar as CalendarIcon, Clock, MapPin, Users as UsersIcon, AlertCircle, CheckCircle2, Edit } from 'lucide-react';
import { formatDate, getEventTypeColor } from '../utils/helpers';

// Common required roles for wedding events — used to highlight gaps in crew coverage
const DEFAULT_REQUIRED_ROLES = ['Photographer', 'Videographer'];

const getEventMissingRoles = (assignments = [], requiredRoles = DEFAULT_REQUIRED_ROLES) => {
  const assignedRoles = new Set(
    (assignments || [])
      .filter(a => (a.confirmation_status || 'pending_confirmation') !== 'declined')
      .map(a => (a.crew_role || '').toLowerCase())
  );
  return requiredRoles.filter(role => {
    const r = role.toLowerCase();
    // also accept contains match for roles like "Lead Photographer" etc.
    return ![...assignedRoles].some(ar => ar && (ar === r || ar.includes(r)));
  });
};

const ProjectTimeline = ({ events, eventAssignments, isAdmin, onAssignCrew, onEditEvent }) => {
  // Sort events chronologically by date then start_time
  const parseTime = (t) => {
    if (!t) return 0;
    const parts = t.split(' ');
    const [hh, mm] = parts[0].split(':').map(Number);
    let h = hh || 0;
    const period = parts[1];
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h * 60 + (mm || 0);
  };

  const sorted = [...events].sort((a, b) => {
    const dc = (a.event_date || '').localeCompare(b.event_date || '');
    if (dc !== 0) return dc;
    return parseTime(a.start_time) - parseTime(b.start_time);
  });

  return (
    <div className="relative" data-testid="project-timeline">
      {/* Vertical timeline bar */}
      <div className="absolute left-4 md:left-6 top-2 bottom-2 w-0.5 bg-gold/30" aria-hidden="true" />

      <div className="space-y-6">
        {sorted.map((event, idx) => {
          const assignments = eventAssignments[event.event_id] || [];
          const activeAssignments = assignments.filter(
            a => (a.confirmation_status || 'pending_confirmation') !== 'declined'
          );
          const missingRoles = getEventMissingRoles(assignments);
          const hasGap = missingRoles.length > 0 || activeAssignments.length === 0;

          return (
            <div key={event.event_id} className="relative pl-12 md:pl-16" data-testid={`timeline-item-${event.event_id}`}>
              {/* Timeline dot */}
              <div
                className={`absolute left-2 md:left-4 top-3 w-5 h-5 rounded-full border-4 border-background flex items-center justify-center ${
                  hasGap ? 'bg-amber-500' : 'bg-emerald-500'
                }`}
                style={{ boxShadow: `0 0 0 2px ${getEventTypeColor(event.event_type)}` }}
                aria-hidden="true"
              />
              {/* Step number */}
              <div className="absolute left-0 md:left-0 top-12 text-[10px] font-bold text-muted-foreground w-8 md:w-12 text-center">
                {idx + 1}
              </div>

              <div
                className={`card p-4 md:p-5 border-l-4 ${hasGap ? 'border-l-amber-500' : 'border-l-emerald-500'}`}
                data-testid={`timeline-card-${event.event_id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2 mb-1">
                      <h3 className="text-lg md:text-xl font-medium">{event.event_name}</h3>
                      <span
                        className="text-[10px] px-2 py-0.5 text-white"
                        style={{ backgroundColor: getEventTypeColor(event.event_type) }}
                      >
                        {event.event_type}
                      </span>
                      {hasGap ? (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:bg-amber-950 dark:text-amber-300">
                          <AlertCircle size={10} /> Needs crew
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">
                          <CheckCircle2 size={10} /> Staffed
                        </span>
                      )}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <CalendarIcon size={12} /> {formatDate(event.event_date)}
                      </span>
                      {event.start_time && (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={12} /> {event.start_time} – {event.end_time}
                        </span>
                      )}
                      {event.location && (
                        <span className="inline-flex items-center gap-1 truncate max-w-[200px]">
                          <MapPin size={12} /> {event.location}
                        </span>
                      )}
                    </div>
                  </div>

                  {isAdmin && (
                    <div className="flex gap-2 flex-shrink-0">
                      <button
                        onClick={() => onAssignCrew(event)}
                        className="p-2 border border-border hover:border-gold transition-colors"
                        title="Assign Crew"
                        data-testid={`timeline-assign-${event.event_id}`}
                      >
                        <UsersIcon size={14} />
                      </button>
                      <button
                        onClick={() => onEditEvent(event)}
                        className="p-2 border border-border hover:border-gold transition-colors"
                        title="Edit"
                      >
                        <Edit size={14} />
                      </button>
                    </div>
                  )}
                </div>

                {/* Assigned crew */}
                <div className="pt-3 border-t border-border">
                  <div className="flex items-center gap-1.5 text-xs font-medium mb-2">
                    <UsersIcon size={12} />
                    <span>Assigned Crew ({activeAssignments.length})</span>
                  </div>
                  {activeAssignments.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {activeAssignments.map((a) => {
                        const conf = a.confirmation_status || 'pending_confirmation';
                        const statusColor =
                          conf === 'accepted'
                            ? 'bg-emerald-100 dark:bg-emerald-500/15 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300'
                            : conf === 'declined'
                            ? 'bg-red-100 dark:bg-red-500/15 text-red-800 dark:bg-red-950 dark:text-red-300'
                            : 'bg-amber-100 dark:bg-amber-500/15 text-amber-800 dark:bg-amber-950 dark:text-amber-300';
                        return (
                          <div
                            key={a.assignment_id}
                            className="inline-flex items-center gap-2 px-2 py-1 border border-border text-xs"
                          >
                            <span className="font-medium">{a.crew_member_name}</span>
                            {a.crew_role && (
                              <span className="text-[10px] text-muted-foreground">({a.crew_role})</span>
                            )}
                            <span className={`text-[10px] px-1.5 py-0.5 ${statusColor}`}>
                              {conf === 'accepted' ? 'Accepted' : conf === 'declined' ? 'Declined' : 'Pending'}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-xs text-muted-foreground">No crew assigned yet</p>
                  )}

                  {missingRoles.length > 0 && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5" data-testid={`timeline-missing-${event.event_id}`}>
                      <span className="text-[10px] uppercase tracking-wide text-amber-700 dark:text-amber-400 font-medium">
                        Missing:
                      </span>
                      {missingRoles.map((role) => (
                        <span
                          key={role}
                          className="text-[10px] px-2 py-0.5 bg-amber-50 dark:bg-amber-500/10 text-amber-800 dark:text-amber-300 border border-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:border-amber-900"
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProjectTimeline;
